import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container/Container';
import TextField from '@material-ui/core/TextField';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Button from '@material-ui/core/Button';
import { MTableToolbar } from '@material-table/core'; //MTableBodyRow
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';

import { searchInStrings } from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import Background from 'components/Layout/Background';

import { RootStore } from 'stores/rootStore';
import { AdminInstrumentsStore } from 'stores/admin/adminInstruments';
import { realms } from 'helpers/realms';

interface Stores {
  rootStore: RootStore;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actions: {
      padding: theme.spacing(2),
      display: 'flex',
      justifyContent: 'center',
      '& > button': {
        margin: theme.spacing(0, 1),
      },
    },
  }),
);

const Instruments: React.FC<Stores> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new AdminInstrumentsStore(rootStore));
  const intermediaries = Object.values(realms).filter((r) => !['core', 'endusers'].includes(r));
  const classes = useStyles();
  const tableRef = React.createRef<any>();

  return (
    <>
      <Container maxWidth="md">
        <Background inside>
          <TextField
            name="intermediaryCode"
            select={true}
            defaultValue={''}
            onChange={async (e) => await store.fetchList(e.target.value)}
            label={t('backOffice_advisors_intermediaryCode')}
            disabled={store.editing}
          >
            {intermediaries.map((i) => (
              <MenuItem key={i} value={i.toUpperCase()}>
                {t(`intermediary_${i.toUpperCase()}_short`)}
              </MenuItem>
            ))}
          </TextField>
          {store.list.length > 0 && (
            <MaterialTable
              tableRef={tableRef}
              search
              options={{
                sorting: true,
                draggable: false,
                pageSize: store.list.length,
                paging: false,

                rowStyle: (rowData, index, level) => {
                  return { color: rowData.locked ? '#757575' : '#000' };
                },
              }}
              components={{
                Toolbar: (props) => (
                  <Grid container>
                    <Grid item xs={12}>
                      <MTableToolbar {...props} />
                    </Grid>
                  </Grid>
                ),
              }}
              data={store.list}
              columns={[
                {
                  field: 'title',
                  title: t('accountPlan_portfolioTable_asset'),
                  cellStyle: { width: '33%' },
                  customFilterAndSearch: (filter, rowData, def) =>
                    searchInStrings(filter, [rowData.title, rowData.isin, rowData.code]),
                },
                { field: 'code', title: t('general_code') },
                { field: 'isin', title: t('accountPlan_portfolioTable_ISIN') },
                { field: 'locked', defaultSort: 'asc', hidden: true },
                {
                  field: 'enabled',
                  render: (asset) => (
                    <>
                      <Checkbox
                        checked={store.list[store.list.findIndex((i) => i.code === asset.code)].enabled}
                        onChange={(e) => {
                          store.setInstrument(asset.code, e.target.checked);
                        }}
                        disabled={asset.locked}
                      />
                    </>
                  ),
                },
              ]}
            />
          )}

          {store.editing && (
            <div className={classes.actions}>
              <Button
                onClick={() => {
                  store.restoreOriginal();
                }}
                color="default"
                variant="contained"
                startIcon={<ArrowBackIos />}
              >
                {t('general_cancel')}
              </Button>
              <Button
                onClick={async () => {
                  await store.save();
                }}
                color="primary"
                variant="contained"
                endIcon={<ArrowForwardIos />}
              >
                {t('general_save')}
              </Button>
            </div>
          )}
        </Background>
      </Container>
    </>
  );
};

export default compose(inject('rootStore'), observer)(Instruments);
